// @flow
import React from 'react'
import classNames from 'classnames'
import { isEmpty } from 'ramda'
import AbstractFormField from './AbstractFormField'
import type { DefaultFormFieldType } from './AbstractFormField'
import { formatInputValue } from '../../InputHelpFunctions'
import type { InputType } from '../../Types/InputTypes'
import type { ValidationErrorType } from '../../Types/ValidationError'
import { HTMLInputTypes } from '../../Enums'
import FieldError from '../form/Error'

export type NumberProps = DefaultFormFieldType & {
  domain: {
    _type: string,
    max?: number,
    min?: number,
    step?: number,
    precision?: number,
  },
  enabled?: boolean,
  inputType?: InputType,
  id: string,
  inputClassName?: string,
  maxLength?: number,
  name: string,
  onChange: (value: string) => void,
  textMode?: boolean,
  textModeClassName?: string,
  validationErrors: ValidationErrorType,
  value: number | string,
}

export type NumberState = {
  oldValue: string,
  prevIsPristine: boolean,
  prevPropsValue: string,
  pristine: boolean,
  value: string,
}

export default class Number extends AbstractFormField<NumberProps, NumberState> {
  static defaultProps = {
    inputType: HTMLInputTypes.NUMBER,
  }

  static getDerivedStateFromProps(nextProps: NumberProps, prevState: NumberState) {
    return getDerivedStateFromPropsLogic(nextProps, prevState)
  }

  constructor(props: NumberProps) {
    super(props)

    this.state = {
      oldValue: '',
      prevIsPristine: true,
      prevPropsValue: '',
      pristine: true,
      value: ['string', 'number'].includes(typeof props.value) ? props.value : '',
    }
  }

  handleOnBlur = (event: SyntheticInputEvent<HTMLInputElement>) => {
    const actualValue = event.target.value

    this.onBlur(event)

    if (!actualValue) {
      this.props.onChange(actualValue)
      return
    }

    const value = this.getUpdatedValue(this.formatValue(actualValue))

    this.setState({
      value,
    })

    this.props.onChange(value)
  }

  formatValue = (actualValue: string): number => {
    const parser = this.getParserFunction()

    return parser(formatInputValue(this.props.inputType, actualValue).replace(',', '.'))
  }

  getParserFunction = (): Function => {
    return this.props.domain._type === 'float' ? parseFloat : (value) => parseInt(value, 10)
  }

  getUpdatedValue = (formattedValue: number): number => {
    const step = this.props.domain.step || 1

    return Math.round(formattedValue / step) * step
  }

  handleOnChange = (event: SyntheticInputEvent<HTMLInputElement>) => {
    if (event.target.value.match(/^(\d|\s|\.)*,?\d{0,2}$/g)) {
      this.setState({ value: event.target.value })
    }
  }

  render() {
    const { textMode, enabled, id, inputClassName, name, prefix, templateVariables, validationErrors } = this.props

    if (textMode) {
      return this.getTextModeLabel()
    }

    const error = !this.isPristine() && validationErrors && !isEmpty(validationErrors[name]) && validationErrors[name]
    const classes = ['form-control', inputClassName || '', error && 'is-invalid'] // TODO handle errors
    return (
      <div>
          <input
            className={classNames(classes)}
            data-hj-whitelist
            id={id}
            name={name}
            onBlur={this.handleOnBlur}
            onChange={this.handleOnChange}
            readOnly={!enabled}
            type={HTMLInputTypes.TEXT}
            value={this.state.value}
          />
        <FieldError
          error={error}
          name={name}
          onHideError={this.hideError}
          prefix={prefix}
          templateVariables={templateVariables}
        />
      </div>
    )
  }

  getTextModeLabel = () => {
    return <span className={this.props.textModeClassName}>{this.props.value}</span>
  }

  getCssClasses = (): Array<string> => {
    const classes = ['form-control']

    if (this.hasErrors() && !this.isPristine()) {
      classes.push('is-invalid')
    }

    return classes
  }
}

export const getDerivedStateFromPropsLogic = (nextProps: NumberProps, prevState: NumberState): Object => {
  let state = AbstractFormField.getDerivedStateFromProps(nextProps, prevState)

  if (nextProps.value !== prevState.oldValue && typeof nextProps.value !== 'undefined') {
    let newState: Object = {}

    if (state) {
      newState = { ...state }
    }

    let value = typeof nextProps.value === 'undefined' ? '' : nextProps.value

    newState.value = value
    newState.oldValue = value

    return newState
  }

  return state
}
